const id = (n) => `00000000-0000-4000-8000-${String(n).padStart(12, '0')}`;
export const RIDER_DEBT_FIXTURE_RIDERS = {
    ana: id(1), bruno: id(2), carla: id(3), dani: id(4), gil: id(5),
};
const ago = (asOf, days, hours = 0) => new Date(Date.parse(asOf) - days * 86_400_000 - hours * 3_600_000).toISOString();
export function buildRiderDebtFixture(asOf = '2026-03-10T02:00:00.000Z') {
    const R = RIDER_DEBT_FIXTURE_RIDERS;
    const person = {
        [R.ana]: { riderRef: 'psn-ana', riderName: 'Ana Alpha', phoneMasked: '+00 ** ****-1111' },
        [R.bruno]: { riderRef: 'psn-bruno', riderName: 'Bruno Brito', phoneMasked: '+00 ** ****-2222' },
        [R.carla]: { riderRef: 'psn-carla', riderName: 'Carla Costa', phoneMasked: '+00 ** ****-3333' },
        [R.dani]: { riderRef: 'psn-dani', riderName: 'Dani Dias', phoneMasked: '+00 ** ****-4444' },
        [R.gil]: { riderRef: 'psn-gil', riderName: 'Gil Gomes', phoneMasked: '+00 ** ****-5555' },
    };
    const debt = (n, o) => {
        const createdAt = ago(asOf, o.daysAgo);
        const settled = o.settled ?? 0n;
        const status = settled === 0n ? 'OPEN' : settled === o.amount ? 'SETTLED' : 'PARTIAL';
        const p = o.rider === null ? { riderRef: null, riderName: null, phoneMasked: null } : person[o.rider];
        return {
            market: o.market, debtId: `debt-${n}`, origin: o.origin ?? 'CASH_SHORTFALL', reasonCode: o.reason ?? (o.origin === 'CANCELLATION_FEE' ? 'CANCEL_FEE_AFTER_ASSIGNMENT' : 'RIDER_DID_NOT_PAY'),
            policyVersion: o.rider === null ? null : 'payment.cashNonPayment@v1', originRideId: o.rider === null ? null : `ride-o-${n}`, originRideCode: o.rider === null ? null : `RC-${1000 + n}`,
            riderId: o.rider, riderRef: p.riderRef, riderName: p.riderName, phoneMasked: p.phoneMasked, currency: o.currency, amountMinor: o.amount, settledMinor: settled, status,
            createdAt, dateKey: createdAt.slice(0, 10), lastSettledAt: o.lastPaidDaysAgo == null ? null : ago(asOf, o.lastPaidDaysAgo), settledAt: status === 'SETTLED' && o.settledDaysAgo != null ? ago(asOf, o.settledDaysAgo) : null,
        };
    };
    const pay = (n, d, amount, daysAgo) => {
        const paidAt = ago(asOf, daysAgo);
        return { market: d.market, paymentId: `pay-${n}`, debtId: d.debtId, origin: d.origin, currency: d.currency, settlingRideId: d.riderId === null ? null : `ride-s-${n}`, settlingRideCode: d.riderId === null ? null : `RC-${2000 + n}`, amountMinor: amount, paidAt, dateKey: paidAt.slice(0, 10) };
    };
    const A = 'zz:alpha';
    const B = 'zz:beta';
    const G = 'yy:gamma';
    const d1 = debt(1, { market: A, currency: 'USD', rider: R.ana, amount: 1500n, daysAgo: 3 });
    const d2 = debt(2, { market: A, currency: 'USD', rider: R.ana, amount: 2500n, settled: 1000n, daysAgo: 40, lastPaidDaysAgo: 5 });
    const d3 = debt(3, { market: A, currency: 'USD', rider: R.ana, origin: 'CANCELLATION_FEE', amount: 600n, settled: 600n, daysAgo: 70, lastPaidDaysAgo: 20, settledDaysAgo: 20 });
    const d4 = debt(4, { market: A, currency: 'USD', rider: R.bruno, origin: 'CANCELLATION_FEE', amount: 800n, daysAgo: 12 });
    const d5 = debt(5, { market: A, currency: 'USD', rider: R.carla, amount: 5000n, daysAgo: 90 });
    const d6 = debt(6, { market: A, currency: 'USD', rider: null, amount: 300n, daysAgo: 8 });
    const d7 = debt(7, { market: B, currency: 'EUR', rider: R.dani, amount: 2000n, daysAgo: 5 });
    const d8 = debt(8, { market: B, currency: 'EUR', rider: R.dani, amount: 1200n, settled: 1200n, daysAgo: 35, lastPaidDaysAgo: 2, settledDaysAgo: 2 });
    const d9 = debt(9, { market: G, currency: 'EUR', rider: R.gil, amount: 700n, daysAgo: 1 });
    const debts = [d1, d2, d3, d4, d5, d6, d7, d8, d9];
    return {
        debts,
        payments: [
            pay(1, d2, 1000n, 5),
            pay(2, d3, 400n, 30),
            pay(3, d3, 200n, 20),
            pay(4, d8, 700n, 10),
            pay(5, d8, 500n, 2),
        ],
    };
}
const floorId = (n) => `00000000-0000-4000-8000-${String(900 + n).padStart(12, '0')}`;
/**
 * A market whose months sit on both sides of the privacy floor (D536 review P2-1), one currency, fixed dates:
 *  - 2026-02: ONE rider with a cancellation fee of 600 created on the 10th and 200 of it paid on the 18th (a cell of one person), next to five riders with a cash shortfall;
 *  - 2026-03: five riders with a cancellation fee (300 each) and six with a cash shortfall (1000 each), five of whom paid 400 each on the 8th (every cell has at least five riders).
 * `zz:alpha`, USD. Ids and instants are deterministic; the age buckets are relative to the `asOf` the reader is given.
 */
export function buildRiderDebtFloorFixture() {
    const M = 'zz:alpha';
    const debts = [];
    const payments = [];
    const mk = (n, o) => {
        const settled = o.settled ?? 0n;
        const rider = floorId(o.rider);
        debts.push({
            market: M, debtId: `fdebt-${n}`, origin: o.origin, reasonCode: o.origin === 'CANCELLATION_FEE' ? 'CANCEL_FEE_AFTER_ASSIGNMENT' : 'RIDER_DID_NOT_PAY', policyVersion: 'payment.cashNonPayment@v1',
            originRideId: `fride-o-${n}`, originRideCode: `FC-${n}`, riderId: rider, riderRef: `fpsn-${o.rider}`, riderName: `Rider ${o.rider}`, phoneMasked: `+00 ** ****-${String(1000 + o.rider)}`,
            currency: 'USD', amountMinor: o.amount, settledMinor: settled, status: settled === 0n ? 'OPEN' : settled === o.amount ? 'SETTLED' : 'PARTIAL', createdAt: o.createdAt, dateKey: o.createdAt.slice(0, 10),
            lastSettledAt: o.paidAt ?? null, settledAt: settled === o.amount && o.paidAt !== undefined ? o.paidAt : null,
        });
        if (settled > 0n && o.paidAt !== undefined) {
            payments.push({ market: M, paymentId: `fpay-${n}`, debtId: `fdebt-${n}`, origin: o.origin, currency: 'USD', settlingRideId: `fride-s-${n}`, settlingRideCode: `FS-${n}`, amountMinor: settled, paidAt: o.paidAt, dateKey: o.paidAt.slice(0, 10) });
        }
    };
    // February: one person's fee (paid in part) + five cash shortfalls nobody has paid
    mk(1, { rider: 1, origin: 'CANCELLATION_FEE', amount: 600n, settled: 200n, createdAt: '2026-02-10T12:00:00.000Z', paidAt: '2026-02-18T12:00:00.000Z' });
    for (let r = 2; r <= 6; r += 1)
        mk(10 + r, { rider: r, origin: 'CASH_SHORTFALL', amount: 700n, createdAt: '2026-02-11T12:00:00.000Z' });
    // March: five fees, six shortfalls, five of the six shortfalls paid 400 each
    for (let r = 1; r <= 5; r += 1)
        mk(20 + r, { rider: r, origin: 'CANCELLATION_FEE', amount: 300n, createdAt: '2026-03-03T12:00:00.000Z' });
    for (let r = 1; r <= 6; r += 1)
        mk(30 + r, { rider: r, origin: 'CASH_SHORTFALL', amount: 1000n, ...(r <= 5 ? { settled: 400n, paidAt: '2026-03-08T12:00:00.000Z' } : {}), createdAt: '2026-03-02T12:00:00.000Z' });
    return { debts, payments };
}
//# sourceMappingURL=rider-debts-fixture.js.map